"use client";

import * as React from "react";
import type { CellValue, TableValueType } from "@quovy/types";
import type { Citation } from "@quovy/field-types";
import { InlineEditPopover } from "../../inline-edit-popover";
import { ValueEditor } from "../../value-editor";

export interface InlineEditProps {
  fieldType: TableValueType;
  value: CellValue;
  /** Field-type-specific options (e.g. enum options, currency code). */
  options?: unknown;
  /** Real citations from the cell. Shown in the editor popover. */
  citations?: Citation[];
  /** Controlled editing toggle owned by the grid (focused cell, Enter, etc). */
  editing: boolean;
  onEditingChange: (next: boolean) => void;
  onCommit: (next: CellValue) => void | Promise<void>;
  onCancel?: () => void;
  onCitationSelect?: (id: string) => void;
  onCitationView?: (id: string) => void;
  disabled?: boolean;
  /** Read-only cell body. Stays mounted at all times; popover anchors to it. */
  children: React.ReactNode;
}

/**
 * Renders the read-only cell body always; opens an anchored popover
 * containing the registry-resolved `ValueEditor` plus the citation list
 * when `editing` is true. Delegates anchoring + portal semantics to the
 * shared `InlineEditPopover` so detail-page FieldValue uses the same scaffold.
 */
export function InlineEdit({
  fieldType,
  value,
  options,
  citations,
  editing,
  onEditingChange,
  onCommit,
  onCancel,
  onCitationSelect,
  onCitationView,
  disabled,
  children,
}: InlineEditProps) {
  // `datetime` registers as an alias for `date` in the field-types registry,
  // so pass `fieldType` through unmodified — the registry handles dispatch.
  const editorType = fieldType;

  const handleCommit = React.useCallback(
    (next: unknown) => {
      void onCommit(next as CellValue);
    },
    [onCommit],
  );

  const handleCancel = React.useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const handleClear = React.useCallback(() => {
    void onCommit(null as CellValue);
  }, [onCommit]);

  return (
    <InlineEditPopover
      open={editing}
      onOpenChange={onEditingChange}
      anchor={children}
      {...(citations ? { citations } : {})}
      valueType={fieldType}
      {...(onCitationSelect ? { onCitationSelect } : {})}
      {...(onCitationView ? { onCitationView } : {})}
      // Pass onCommit so InlineEditPopover's default citation-select
      // handler (which commits the picked citation's snippet) flows back
      // through this cell's onCommit → onCommitEdit chain.
      onCommit={handleCommit}
      onCancel={handleCancel}
      {...(disabled ? {} : { onClear: handleClear })}
      editor={({ onCommit: commit, onCancel: cancel }) =>
        disabled ? null : (
          <ValueEditor
            type={editorType}
            value={value}
            {...(options !== undefined ? { options } : {})}
            // Only call `commit` — `InlineEditPopover.handleCommit` already
            // fans out to this cell's `onCommit` prop (wired as the
            // popover's `onCommit`) and closes the popover. Calling our
            // local `handleCommit` here as well caused `onCommitEdit` to
            // fire twice per Enter / blur-commit, which round-tripped two
            // identical PATCHes through the writeMutation chain.
            onCommit={commit}
            onCancel={cancel}
          />
        )
      }
    />
  );
}
